import React, { useEffect, useState } from 'react';
import { Redirect } from 'react-router-dom';
import { FormattedMessage, useIntl } from 'react-intl';
import { Table, TableBody, TableContainer, TableHead, TableRow } from '@material-ui/core';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import UpIcon from '@material-ui/icons/KeyboardArrowUp';
import LimpiarFiltros from '../../assets/images/limpiar-filtros.svg';
import '../../assets/css/vec-datatables.css';
import Security from '../../commons/security/Security.js';
import ConfigBusiness from '../../commons/config/ConfigBusiness.js';
import * as Utils from '../../commons/utils/Utils';
import { StyledTableCellProps as StyledTableCell } from '../../commons/utils/TableStyles';
import { buildEmptyFilterState, serializeFilterState } from '../../commons/filters/GridFilterPanel.jsx';
import GridSearchResultsBadge from '../../commons/filters/GridSearchResultsBadge.jsx';
import GridPagination from '../../commons/grid/GridPagination.jsx';
import { GridToolbar, GridToolbarButton } from '../../commons/grid/GridToolbar.jsx';
import { GridEmptyRow, GridLoadingRow } from '../../commons/grid/GridStateRow.jsx';
import SortableCell from '../../commons/grid/SortableCell.jsx';
import GridColumnSelector from '../../commons/grid/GridColumnSelector.jsx';
import useColumnVisibility from '../../commons/grid/useColumnVisibility.js';
import useGridState from '../../commons/grid/useGridState.js';
import buildQueryString from '../../commons/grid/buildQueryString.js';
import SolicitudesCards from './SolicitudesCards.jsx';
import SolicitudesGridFiltros from './SolicitudesGridFiltros.jsx';
import SolicitudesGridRow from './SolicitudesGridRow.jsx';
import SolicitudDetalleModal from './SolicitudDetalleModal.jsx';
import SolicitudAdjuntosModal from './SolicitudAdjuntosModal.jsx';
import { buildSolicitudesFilterFields, getSolicitudesFilterDefaults } from './solicitudesFilterFields.js';

const COLUMNAS_STORAGE_KEY = 'SolicitudesGrid_columnas';
const CAMPOS_ORDENABLES = ['fecha', 'estado', 'solicitante', 'entidad'];

const queryVacia = (intl) => {
	const fields = buildSolicitudesFilterFields(intl);
	return serializeFilterState(fields, buildEmptyFilterState(fields));
};

const queryPorDefecto = (intl) => {
	const fields = buildSolicitudesFilterFields(intl);
	return serializeFilterState(fields, Object.assign({}, buildEmptyFilterState(fields), getSolicitudesFilterDefaults(intl)));
};

const SolicitudesGrid = (props) => {
	const intl = useIntl();
	const inmovilizadoHabilitado = ConfigBusiness.get('tickets.inmovilizado.habilitado') === 'true';

	const columnas = useColumnVisibility(
		[
			{ name: 'numero', label: intl.formatMessage({ id: 'solicitudesGrid.column.solicitud', defaultMessage: 'Solicitud' }) },
			{ name: 'estado', label: intl.formatMessage({ id: 'solicitudesGrid.column.estado', defaultMessage: 'Estado' }) },
			{ name: 'fecha', label: intl.formatMessage({ id: 'solicitudesGrid.column.creado', defaultMessage: 'Creado' }) },
			{ name: 'solicitante', label: intl.formatMessage({ id: 'solicitudesGrid.column.por', defaultMessage: 'Por' }) },
			{ name: 'entidad', label: intl.formatMessage({ id: 'solicitudesGrid.column.entidad', defaultMessage: 'Entidad' }) },
			{ name: 'inmovilizado', label: intl.formatMessage({ id: 'correctivosAbm.datos_generales.inmovilizado', defaultMessage: 'Inmovilizado' }), enabled: inmovilizadoHabilitado },
			{ name: 'base', label: intl.formatMessage({ id: 'solicitudesGrid.column.base', defaultMessage: 'Base' }) },
			{ name: 'detalle', label: intl.formatMessage({ id: 'solicitudesGrid.column.detalle', defaultMessage: 'Detalle' }) },
			{ name: 'adjuntos', label: intl.formatMessage({ id: 'solicitudesGrid.column.adjuntos', defaultMessage: 'Adjuntos' }) },
			{ name: 'ticket', label: intl.formatMessage({ id: 'solicitudesGrid.column.ticket', defaultMessage: 'Ticket' }) },
		],
		COLUMNAS_STORAGE_KEY
	);
	const grid = useGridState({ campos: CAMPOS_ORDENABLES, sortInicial: { campo: 'fecha', direccion: 'desc' } });

	const [redirectTo, setRedirectTo] = useState(null);
	const [loading, setLoading] = useState(false);
	const [dropdownActive, setDropdownActive] = useState(false);
	const [rows, setRows] = useState([]);
	const [count, setCount] = useState(0);
	const [conteos, setConteos] = useState(null);
	const [filterQuery, setFilterQuery] = useState(() => queryPorDefecto(intl));
	const [searchActive, setSearchActive] = useState(false);
	const [filterResetInputs, setFilterResetInputs] = useState(false);
	const [overrideSignal, setOverrideSignal] = useState(null);
	const [detalleAbierto, setDetalleAbierto] = useState(null);
	const [adjuntosAbiertos, setAdjuntosAbiertos] = useState(null);

	const habilitado = ConfigBusiness.get('mantenimientos.solicitudes.habilitado') === 'true' && Security.hasPermission('SOLICITUDES_LISTAR');

	useEffect(() => {
		if (!habilitado) {
			setRedirectTo('/error');
			return undefined;
		}
		let vigente = true;
		setLoading(true);
		const sort = {};
		CAMPOS_ORDENABLES.forEach((campo) => {
			sort[campo + 'Sort'] = grid.sortQuery[campo] || '';
		});
		const params = Object.assign({ page: grid.pagination.page + 1, perPage: grid.pagination.perPage }, filterQuery, sort);

		Utils.getData('/solicitudes' + buildQueryString(params))
			.then((data) => {
				if (!vigente) return;
				setRows((data && data.items) || []);
				setCount((data && data.pagination && data.pagination.count) || 0);
				setConteos((data && data.conteos) || null);
			})
			.catch(() => {})
			.finally(() => {
				if (vigente) setLoading(false);
			});

		return () => {
			vigente = false;
		};
	}, [grid.pagination, grid.sort, filterQuery]);

	const handleApplyFilters = (query) => {
		const porDefecto = queryPorDefecto(intl);
		setSearchActive(Object.keys(query).some((k) => (query[k] || '') !== (porDefecto[k] || '')));
		grid.setPage(0);
		setFilterQuery(query);
	};

	const handleSearchClear = () => {
		grid.setPage(0);
		setFilterQuery(queryPorDefecto(intl));
		setSearchActive(false);
		setFilterResetInputs(true);
	};

	const handleFilterReset = () => {
		grid.reset();
		columnas.mostrarTodas();
		setDropdownActive(false);
		handleSearchClear();
	};

	const handleCardFilter = (estado) => {
		if (filterQuery.estado === estado && !filterQuery.fechaDesde) {
			handleSearchClear();
			return;
		}
		grid.setPage(0);
		setFilterQuery(Object.assign({}, queryVacia(intl), { estado: estado }));
		setSearchActive(true);
		setOverrideSignal({ estado: estado });
	};

	const columnasVisibles = () =>
		2 + ['numero', 'estado', 'fecha', 'solicitante', 'entidad', 'inmovilizado', 'base', 'detalle', 'adjuntos', 'ticket'].filter((c) => columnas.esVisible(c)).length;

	if (redirectTo) return <Redirect push to={redirectTo} />;

	return (
		<React.Fragment>
			<SolicitudesCards conteos={conteos} onFilter={handleCardFilter} />
			<SolicitudDetalleModal solicitud={detalleAbierto} onClose={() => setDetalleAbierto(null)} />
			<SolicitudAdjuntosModal solicitud={adjuntosAbiertos} onClose={() => setAdjuntosAbiertos(null)} />
			<div className="card">
				<div className="card-content collpase show">
					<div className="card-body card-dashboard">
						<div className="container-fluid mb-1">
							<div className="row dt-icons">
								<div className="col">
									{Security.hasPermission('SOLICITUDES_CREAR') && (
										<div
											className="btn btn-primary box-shadow-2 btn-round btn-lg btn-dt-main round-icon"
											style={{ height: 55 }}
											onClick={() => props.history.push(props.match.url + '/add')}
										>
											<i className="ft-plus"></i>
										</div>
									)}
									<div
										className="btn btn-grey-blue box-shadow-2 btn-round btn-lg btn-dt-main round-icon"
										onClick={() => setDropdownActive(!dropdownActive)}
									>
										{dropdownActive ? <UpIcon /> : <ExpandMoreIcon />}
									</div>
								</div>
								<div className="col">
									<GridSearchResultsBadge visible={searchActive} mostrados={rows.length} total={count} onReset={() => handleSearchClear()} />
								</div>
								<div className="col">
									<GridToolbar>
										<GridColumnSelector columns={columnas.seleccionables} esVisible={columnas.esVisible} onToggle={columnas.toggle} />
										<GridToolbarButton title={intl.formatMessage({ id: 'solicitudesGrid.tools_restore_filters.title', defaultMessage: 'Restaurar filtros' })} onClick={() => handleFilterReset()}>
											<img id="filterReset" className="filter-reset" src={LimpiarFiltros} alt="" />
										</GridToolbarButton>
									</GridToolbar>
								</div>
							</div>
						</div>

						<SolicitudesGridFiltros
							open={dropdownActive}
							loading={loading}
							resetSignal={filterResetInputs}
							onResetSignalHandled={() => setFilterResetInputs(false)}
							onApply={handleApplyFilters}
							overrideSignal={overrideSignal}
							onOverrideHandled={() => setOverrideSignal(null)}
						/>

						<TableContainer>
							<Table aria-label="solicitudes">
								<TableHead>
									<TableRow>
										<SortableCell field="numero" show={columnas.esVisible('numero')}>
											<FormattedMessage id="solicitudesGrid.column.solicitud" defaultMessage="Solicitud" />
										</SortableCell>
										<StyledTableCell style={{ width: 5 }} />
										<SortableCell field="estado" sort={grid.sortQuery} onSort={grid.requestSort} show={columnas.esVisible('estado')}>
											<FormattedMessage id="solicitudesGrid.column.estado" defaultMessage="Estado" />
										</SortableCell>
										<SortableCell field="fecha" sort={grid.sortQuery} onSort={grid.requestSort} show={columnas.esVisible('fecha')}>
											<FormattedMessage id="solicitudesGrid.column.creado" defaultMessage="Creado" />
										</SortableCell>
										<SortableCell field="solicitante" sort={grid.sortQuery} onSort={grid.requestSort} show={columnas.esVisible('solicitante')}>
											<FormattedMessage id="solicitudesGrid.column.por" defaultMessage="Por" />
										</SortableCell>
										<SortableCell field="entidad" sort={grid.sortQuery} onSort={grid.requestSort} show={columnas.esVisible('entidad')}>
											<FormattedMessage id="solicitudesGrid.column.entidad" defaultMessage="Entidad" />
										</SortableCell>
										<SortableCell field="inmovilizado" show={columnas.esVisible('inmovilizado')}>
											<FormattedMessage id="correctivosAbm.datos_generales.inmovilizado" defaultMessage="Inmovilizado" />
										</SortableCell>
										<SortableCell field="base" show={columnas.esVisible('base')}>
											<FormattedMessage id="solicitudesGrid.column.base" defaultMessage="Base" />
										</SortableCell>
										<SortableCell field="detalle" show={columnas.esVisible('detalle')}>
											<FormattedMessage id="solicitudesGrid.column.detalle" defaultMessage="Detalle" />
										</SortableCell>
										<SortableCell field="adjuntos" show={columnas.esVisible('adjuntos')}>
											<FormattedMessage id="solicitudesGrid.column.adjuntos" defaultMessage="Adjuntos" />
										</SortableCell>
										<SortableCell field="ticket" show={columnas.esVisible('ticket')}>
											<FormattedMessage id="solicitudesGrid.column.ticket" defaultMessage="Ticket" />
										</SortableCell>
										<StyledTableCell align="center">
											<FormattedMessage id="solicitudesGrid.column.acciones" defaultMessage="Acciones" />
										</StyledTableCell>
									</TableRow>
								</TableHead>
								<TableBody>
									<GridLoadingRow visible={loading} columns={columnasVisibles()} />
									{!loading &&
										(rows.length > 0 ? (
											rows.map((solicitud) => (
												<SolicitudesGridRow key={solicitud.id} solicitud={solicitud} columnas={columnas} onVerDetalle={setDetalleAbierto} onVerAdjuntos={setAdjuntosAbiertos} />
											))
										) : (
											<GridEmptyRow columns={columnasVisibles()}>
												<FormattedMessage id="solicitudesGrid.sin_resultados" defaultMessage="No se encontraron solicitudes con los filtros seleccionados." />
											</GridEmptyRow>
										))}
								</TableBody>
							</Table>
						</TableContainer>

						<GridPagination
							count={count}
							page={grid.pagination.page}
							perPage={grid.pagination.perPage}
							onPageChange={(event, page) => grid.setPage(page)}
							onPerPageChange={(event) => grid.setPerPage(parseInt(event.target.value, 10))}
						/>
					</div>
				</div>
			</div>
		</React.Fragment>
	);
};

export default SolicitudesGrid;
